import { Shield, Brain, Wand2 } from 'lucide-react';
import { motion } from 'framer-motion';
import FiveForcesChecklist from './FiveForcesChecklist';
import { useState, useCallback, useEffect } from 'react';
import { calculateIndustryAttractiveness } from '../../../services/scoringService';
import { companiesApi } from '../../../services/api';

// AI Analysis Animation Component
const AIAnalysisIndicator = ({ isAnalyzing }: { isAnalyzing: boolean }) => {
  if (!isAnalyzing) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
      >
        <Brain className="h-4 w-4" />
      </motion.div>
      <span className="text-sm font-medium">Analyzing with AI</span>
      <motion.div
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <Wand2 className="h-3 w-3" />
      </motion.div>
    </motion.div>
  );
};

interface FiveForcesSectionProps {
  companyId: string;
  onProgressChange?: (progress: number) => void;
}

// Function to convert score to intensity level
const getIntensityLevel = (score: number): string => {
  // -1 indicates no analysis has been done
  if (score < 0) return 'Not Analyzed';

  if (score >= 80) return 'Very Weak';
  if (score >= 60) return 'Weak';
  if (score >= 40) return 'Moderate';
  if (score >= 20) return 'Strong';
  return 'Very Strong';
};

// Function to get color class for intensity level
const getIntensityColor = (level: string): string => {
  switch (level) {
    case 'Very Strong':
      return 'bg-red-100 text-red-800';
    case 'Strong':
      return 'bg-orange-100 text-orange-800';
    case 'Moderate':
      return 'bg-yellow-100 text-yellow-800';
    case 'Weak':
      return 'bg-green-100 text-green-800';
    case 'Very Weak':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const fiveForces = [
  {
    key: 'suppliers',
    title: 'Supplier Power',
    description: 'How much power and control suppliers have',
    entityType: 'competition.suppliers',
    tableLabel: 'Suppliers',
  },
  {
    key: 'buyers',
    title: 'Buyer Power',
    description: 'How much bargaining power customers have',
    entityType: 'competition.buyers',
    tableLabel: 'Buyers',
  },
  {
    key: 'rivalFirms',
    title: 'Competitive Rivalry',
    description: 'The intensity of competition among existing rivals',
    entityType: 'competition.rival_firms',
    tableLabel: 'Rival Firms',
  },
  {
    key: 'substitutes',
    title: 'Threat of Substitutes',
    description: 'How easily customers can find alternatives',
    entityType: 'competition.substitute_products',
    tableLabel: 'Substitute Products',
  },
  {
    key: 'newEntrants',
    title: 'Threat of New Entrants',
    description: 'How easy it is for new companies to enter the market',
    entityType: 'competition.potential_new_entrants',
    tableLabel: 'Potential New Entrants',
  },
];

export default function FiveForcesSection({ companyId, onProgressChange }: FiveForcesSectionProps) {
  const [activeForce, setActiveForce] = useState('suppliers');
  const [forceScores, setForceScores] = useState<Record<string, number>>({});
  const [backendIndustryAttractiveness, setBackendIndustryAttractiveness] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastChangeTime, setLastChangeTime] = useState<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Calculate and report initial progress based on total answered questions
  const calculateProgress = useCallback(() => {
    // We need to count actual answered questions across all Five Forces
    // Target: 5 answered questions = 100% completion
    const targetAnswers = 5;

    // This is a simplified version - in a real implementation, we'd need to
    // query the questions API to count actual answered questions across all forces
    // For now, we'll use the force scores as a proxy
    const analyzedForces = Object.values(forceScores).filter((score) => score >= 0);
    const estimatedAnsweredQuestions = analyzedForces.length; // Each analyzed force represents some answered questions

    return estimatedAnsweredQuestions >= targetAnswers
      ? 100
      : Math.round((estimatedAnsweredQuestions / targetAnswers) * 100);
  }, [forceScores]);

  // Report initial progress when component mounts or scores change
  useEffect(() => {
    if (onProgressChange) {
      const progress = calculateProgress();
      onProgressChange(progress);
    }
  }, [forceScores, calculateProgress]); // Removed onProgressChange from deps - it's memoized with useCallback

  const handleScoreChange = useCallback(
    (entityKey: string, score: number) => {
      setForceScores((prev) => ({
        ...prev,
        [entityKey]: score,
      }));

      // Only trigger AI analysis animation for user changes, not initial data loading
      if (!isInitialLoad) {
        setIsAnalyzing(true);
        setLastChangeTime(Date.now());
      }
    },
    [isInitialLoad]
  );

  // Fetch backend industry attractiveness to ensure consistency with summary page
  const fetchBackendIndustryAttractiveness = useCallback(async () => {
    try {
      const response = await companiesApi.getSummaryAnalysis(companyId);
      if (response.success && response.data) {
        const summaryData = response.data as any;
        if (summaryData.indicators?.industryAttractiveness) {
          setBackendIndustryAttractiveness(summaryData.indicators.industryAttractiveness);
        }
      }
    } catch (error) {
      console.error('Failed to fetch backend industry attractiveness:', error);
    }
  }, [companyId]);

  // Fetch backend data on initial mount
  useEffect(() => {
    fetchBackendIndustryAttractiveness();
  }, [fetchBackendIndustryAttractiveness]);

  // Set initial load to false after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 1000); // Wait 1 second to allow initial data to load

    return () => clearTimeout(timer);
  }, []);

  // Auto-refresh metrics after 3-second AI analysis animation
  useEffect(() => {
    if (lastChangeTime > 0) {
      // Stop analyzing animation after exactly 3 seconds
      const animationTimer = setTimeout(() => {
        setIsAnalyzing(false);
        // Fetch updated data immediately after animation ends
        fetchBackendIndustryAttractiveness();
      }, 3000); // 3 seconds of AI analysis animation

      return () => clearTimeout(animationTimer);
    }
  }, [lastChangeTime, fetchBackendIndustryAttractiveness]);

  return (
    <div className="strategy-framework-section">
      {/* Summary Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="strategy-framework-summary"
      >
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-600">Industry Attractiveness:</span>
              {isAnalyzing ? (
                <AIAnalysisIndicator isAnalyzing={true} />
              ) : (
                (() => {
                  // Check if any forces have been analyzed (score >= 0)
                  const validScores = Object.values(forceScores).filter((score) => score >= 0);

                  // If no forces have been analyzed, show "Not Analyzed"
                  if (validScores.length === 0) {
                    return (
                      <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                        Not Analyzed
                      </span>
                    );
                  }

                  // Use backend result if available, otherwise fall back to frontend calculation
                  let attractivenessResult;

                  if (backendIndustryAttractiveness) {
                    // Use backend result for consistency with summary page
                    attractivenessResult = backendIndustryAttractiveness;
                  } else {
                    // Fall back to frontend calculation
                    attractivenessResult = calculateIndustryAttractiveness(validScores);
                  }

                  return (
                    <span
                      className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${attractivenessResult.color}`}
                    >
                      {attractivenessResult.level}
                    </span>
                  );
                })()
              )}
            </div>
            <div className="text-xs text-gray-500">
              {(() => {
                const validScores = Object.values(forceScores).filter((score) => score >= 0);
                return `${validScores.length}/5 forces analyzed`;
              })()}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Two Column Layout - Forces Left, Wider Checklist Right */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="strategy-framework-grid"
      >
        {/* Left Side - Force Navigation */}
        <div className="strategy-framework-sidebar flex flex-col space-y-4">
          {/* Force Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center mb-4">
              <Shield className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Five Forces</h2>
              <div className="ml-auto text-right">
                <div className="text-sm text-gray-500">Select a force</div>
              </div>
            </div>

            <div className="space-y-2">
              {fiveForces.map((force) => {
                const forceScore = forceScores[force.key] ?? -1; // Default to -1 (not analyzed)
                const intensity = getIntensityLevel(forceScore);
                const intensityColor = getIntensityColor(intensity);
                const hasProgress = forceScore >= 0;

                return (
                  <button
                    key={force.key}
                    onClick={() => setActiveForce(force.key)}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      activeForce === force.key
                        ? 'bg-blue-100 border-2 border-blue-300'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{force.title}</div>
                        <div className="text-xs text-gray-600 mt-1">{force.description}</div>
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        <div
                          className={`w-2 h-2 rounded-full ${hasProgress ? 'bg-blue-500' : 'bg-gray-300'}`}
                        />
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${intensityColor}`}
                        >
                          {intensity}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Force Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                {fiveForces.find((force) => force.key === activeForce)?.title} Analysis
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {fiveForces.find((force) => force.key === activeForce)?.description}
            </p>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                For each factor, determine if it makes this competitive force{' '}
                <strong>Strong</strong> (bad for your company) or <strong>Weak</strong> (good for
                your company).
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Wider Checklist */}
        <div className="xl:col-span-3">
          {fiveForces.map((force) => (
            <div key={force.key} className={activeForce === force.key ? 'block h-full' : 'hidden'}>
              <FiveForcesChecklist
                entityType={force.entityType}
                entityKey={force.key}
                companyId={companyId}
                title={`${force.title} Analysis`}
                onScoreChange={(score: number) => handleScoreChange(force.key, score)}
                className="h-full"
              />
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
